import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { reportError } from '@/shared';

import {
  documentDraftKeys,
  documentRecipientFromMarkdown,
  FAX_DRAFT_STORE,
  removeDocumentDraft,
  type DocumentDraft,
} from '@/entities/document-draft';

import { sendFax } from '../api/faxWorkflowApi';

export function useFaxSend(patientId: string, draft: DocumentDraft | null, onComplete: () => void) {
  const queryClient = useQueryClient();
  const [sending, setSending] = useState(false);

  const send = useCallback(async () => {
    if (draft === null) {
      return;
    }
    const recipient = documentRecipientFromMarkdown(draft.content.bodyMarkdown);
    setSending(true);
    try {
      await sendFax(patientId, draft);
      toast.success('Fax queued for delivery.', {
        description: `Sent to ${recipient.faxNumber}.`,
      });
      onComplete();

      try {
        await removeDocumentDraft(patientId, draft.id, FAX_DRAFT_STORE);
        queryClient.setQueryData(
          documentDraftKeys.patient(patientId, FAX_DRAFT_STORE),
          (drafts: DocumentDraft[] = []) => drafts.filter((candidate) => candidate.id !== draft.id)
        );
      } catch (cleanupError) {
        reportError(cleanupError, {
          surface: 'handled',
          feature: 'fax-editor',
          operation: 'removeSentFaxDraft',
          extra: { patientId, draftId: draft.id },
        });
        toast.info('Fax sent, but the draft could not be removed.', {
          description: 'It is safe to close this editor and try again later.',
        });
        return;
      }
      try {
        await queryClient.invalidateQueries({ queryKey: ['patient-faxes', 'outbound', patientId] });
      } catch (refreshError) {
        reportError(refreshError, {
          surface: 'handled',
          feature: 'fax-editor',
          operation: 'refreshSentFaxes',
          extra: { patientId, draftId: draft.id },
        });
      }
    } catch (error) {
      reportError(error, {
        surface: 'handled',
        feature: 'fax-editor',
        operation: 'sendFax',
        extra: { patientId, draftId: draft.id },
      });
      toast.info('Fax could not be sent.', {
        description: error instanceof Error ? error.message : 'Please try again.',
      });
    } finally {
      setSending(false);
    }
  }, [draft, onComplete, patientId, queryClient]);

  return { sending, send };
}
